import React, { useEffect, useState, useRef } from "react";
import { Upload, Trash2, Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function GalleryAdmin() {
  const [photos, setPhotos] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [caption, setCaption] = useState("");
  const fileRef = useRef();

  const load = () => api.get("/gallery").then((r) => setPhotos(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const backend = process.env.REACT_APP_BACKEND_URL;
      setImageUrl(`${backend}${res.data.url}`);
      toast.success("Uploaded");
    } catch {
      toast.error("Upload failed");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!imageUrl) return toast.error("Please upload or paste an image URL");
    try {
      await api.post("/gallery", { image_url: imageUrl, caption });
      toast.success("Added to gallery");
      setImageUrl(""); setCaption(""); setShowForm(false);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this photo?")) return;
    await api.delete(`/gallery/${id}`);
    load();
  };

  return (
    <div data-testid="gallery-admin">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif-display text-4xl text-gold-gradient">Gallery</h1>
          <p className="text-sm text-[#D1C7C9]">Upload photos for the public gallery section</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-saffron" data-testid="add-photo-btn">
          <Plus size={14} /> Add Photo
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {photos.map((p) => (
          <div key={p.id} className="relative group rounded-lg overflow-hidden border border-[#333]" data-testid={`gallery-admin-${p.id}`}>
            <img src={p.image_url} alt={p.caption} className="w-full aspect-square object-cover" />
            <button onClick={() => del(p.id)} className="absolute top-2 right-2 p-2 rounded-full bg-red-500/80 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-photo-${p.id}`}>
              <Trash2 size={14} />
            </button>
            {p.caption && <div className="absolute bottom-0 inset-x-0 bg-black/60 p-2 text-xs">{p.caption}</div>}
          </div>
        ))}
        {photos.length === 0 && <p className="text-[#D1C7C9] col-span-full">No photos yet.</p>}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-[#1A1A1A] border border-[#333] rounded-2xl p-6 max-w-md w-full" data-testid="gallery-form-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif-display text-2xl text-gold-gradient">Add Photo</h3>
              <button type="button" onClick={() => setShowForm(false)}><X size={20} className="text-[#D1C7C9]" /></button>
            </div>
            <div className="space-y-3">
              {imageUrl && <img src={imageUrl} alt="" className="w-full h-48 object-cover rounded-md" />}
              <input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="Paste image URL or upload below"
                className="w-full bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-sm text-[#FDFBF7] outline-none focus:border-[#E69138]"
                data-testid="gallery-form-url"
              />
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0])} />
              <button type="button" onClick={() => fileRef.current?.click()} className="btn-outline-gold w-full justify-center text-xs" data-testid="gallery-upload-btn">
                <Upload size={12} /> Upload from device
              </button>
              <input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Caption (optional)"
                className="w-full bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-sm text-[#FDFBF7] outline-none focus:border-[#E69138]"
                data-testid="gallery-form-caption"
              />
            </div>
            <button type="submit" className="btn-saffron w-full justify-center mt-4" data-testid="gallery-save-btn">Add to Gallery</button>
          </form>
        </div>
      )}
    </div>
  );
}
